import { useEffect, useRef, useState } from "react";
import "./styles.scss";
import DOMPurify from "dompurify";
import { Experiments, useExperiment } from "@/modules/experiments/src";

interface HeaderComponentProps {
  headerData?: string;
  hasError: boolean;
}

export const HeaderComponent = ({
  headerData,
  hasError,
}: HeaderComponentProps) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const { isExperimentVariant } = useExperiment();
  const isHeaderEnabledExperiment = isExperimentVariant(
    Experiments.HEADER_FOOTER.name,
    Experiments.HEADER_FOOTER.variants.available
  );
  const [header, setHeader] = useState("");

  useEffect(() => {
    if (!isHeaderEnabledExperiment || !headerData) return;

    let timeoutId: NodeJS.Timeout;
    let isMounted = true;
    const appendedLinks: HTMLLinkElement[] = [];

    const loadStylesheets = (linkTags: NodeListOf<HTMLLinkElement>) => {
      return Array.from(linkTags).map(
        (link) =>
          new Promise<void>((resolve) => {
            const newLink = document.createElement("link");
            newLink.rel = "stylesheet";
            newLink.href = link.href;
            if (link.crossOrigin) newLink.crossOrigin = link.crossOrigin;

            newLink.onload = () => resolve();
            newLink.onerror = () => {
              console.error(`Failed to load stylesheet: ${link.href}`);
              resolve();
            };

            document.head.appendChild(newLink);
            appendedLinks.push(newLink);
          })
      );
    };

    const processHeader = async () => {
      try {
        const sanitizedHeader = DOMPurify.sanitize(headerData, {
          ADD_TAGS: ["link", "script"],
          ADD_ATTR: [
            "href",
            "rel",
            "src",
            "type",
            "crossorigin",
            "onclick",
            "id",
          ],
          ALLOW_UNKNOWN_PROTOCOLS: true,
        });

        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = sanitizedHeader;
        const linkTags = tempDiv.querySelectorAll(
          'link[rel="stylesheet"]'
        ) as NodeListOf<HTMLLinkElement>;

        const styleLoadPromises = loadStylesheets(linkTags);

        const timeoutPromise = new Promise<void>((resolve) => {
          timeoutId = setTimeout(resolve, 5000);
        });

        await Promise.race([
          Promise.allSettled(styleLoadPromises),
          timeoutPromise,
        ]);

        if (!isMounted) return;

        linkTags.forEach((link) => link.remove());

        setHeader(tempDiv.innerHTML);
      } catch (e) {
        console.error("Failed to process header", e);
      }
    };

    processHeader();

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      appendedLinks.forEach((link) => link.remove());
    };
  }, [isHeaderEnabledExperiment, headerData]);

  useEffect(() => {
    if (headerRef.current && header) {
      const scripts = headerRef.current.querySelectorAll("script");
      scripts.forEach((script) => {
        const newScript = document.createElement("script");
        if (script.src) {
          newScript.src = script.src;
        } else {
          newScript.textContent = script.textContent;
        }
        script.parentNode?.replaceChild(newScript, script);
      });
    }
  }, [header]);

  return isHeaderEnabledExperiment && !hasError && header ? (
    <div
      ref={headerRef}
      dangerouslySetInnerHTML={{ __html: header }}
      title="avion-header"
      className="rbc-header"
    />
  ) : (
    <div
      className="rbc-header rbc-header-fallback"
      style={{
        background: "linear-gradient(90deg, #0051a5 0%, #166ae2 100%)",
        height: "60px",
        padding: "15px 0",
        display: "flex",
        alignItems: "center",
      }}
    >
      <div
        style={{
          maxWidth: "1120px",
          width: "100%",
          margin: "0 auto",
          padding: "0 20px",
        }}
      >
        <img
          src="/logo/Desktop/Avion_Logo_EN_5.5.1.svg"
          alt="Avion Logo"
          style={{ height: "30px", width: "auto" }}
        />
      </div>
    </div>
  );
};
